import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck, faSquareMinus } from '@fortawesome/pro-solid-svg-icons';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { EmailLocation } from '../../types';
import styles from './LocationsChecklist.module.scss';

interface LocationsChecklistProps {
    locations: Array<EmailLocation>;
    selectedIds: Array<number>;
    onChange: (ids: Array<number>) => void;
    isDisabled?: (location: EmailLocation) => boolean;
    renderMeta?: (location: EmailLocation) => React.ReactNode;
    className?: string;
    listClassName?: string;
}

type CheckState = 'checked' | 'unchecked' | 'partial';

const CheckIcon: React.FC<{ state: CheckState }> = ({ state }) => (
    <FontAwesomeIcon
        className={clsx(styles.check, { [styles.checkOn]: state !== 'unchecked' })}
        icon={state === 'checked' ? faSquareCheck : state === 'partial' ? faSquareMinus : faSquare}
    />
);

const stateOf = (ids: Array<number>, selectedIds: Array<number>): CheckState => {
    const selected = ids.filter(id => selectedIds.includes(id)).length;
    if (selected === 0) return 'unchecked';
    return selected === ids.length ? 'checked' : 'partial';
};

// Location picker grouped by region. Used by the list filter and the campaign Audience step.
export const LocationsChecklist: React.FC<LocationsChecklistProps> = ({
    locations,
    selectedIds,
    onChange,
    isDisabled,
    renderMeta,
    className,
    listClassName
}) => {
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return locations;
        return locations.filter(location =>
            [location.name, location.city, location.region].some(value => value.toLowerCase().includes(term))
        );
    }, [locations, search]);

    const groups = useMemo(() => {
        const byRegion = new Map<string, Array<EmailLocation>>();
        filtered.forEach(location => {
            byRegion.set(location.region, [...(byRegion.get(location.region) ?? []), location]);
        });
        return Array.from(byRegion.entries());
    }, [filtered]);

    const selectable = (list: Array<EmailLocation>) =>
        list.filter(location => !isDisabled?.(location)).map(location => location.id);

    const allIds = selectable(locations);

    const toggleIds = (ids: Array<number>) => {
        if (ids.length === 0) return;
        const allSelected = ids.every(id => selectedIds.includes(id));
        onChange(
            allSelected ? selectedIds.filter(id => !ids.includes(id)) : Array.from(new Set([...selectedIds, ...ids]))
        );
    };

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.search}>
                <FontAwesomeIcon icon={faSearch} />
                <input
                    value={search}
                    placeholder="Search locations"
                    onChange={event => setSearch(event.target.value)}
                />
            </div>

            {!search && (
                <button
                    type="button"
                    className={clsx(styles.row, styles.allRow)}
                    onClick={() => toggleIds(allIds)}
                >
                    <CheckIcon state={stateOf(allIds, selectedIds)} />
                    <span className={styles.name}>All locations</span>
                    <span className={styles.meta}>
                        {selectedIds.length} of {allIds.length}
                    </span>
                </button>
            )}

            <div className={clsx(styles.list, listClassName)}>
                {groups.map(([region, regionLocations]) => {
                    const regionIds = selectable(regionLocations);

                    return (
                        <div
                            key={region}
                            className={styles.group}
                        >
                            {/* Locations without a region (API data has no address yet) aren't grouped. */}
                            {region && (
                                <button
                                    type="button"
                                    className={clsx(styles.row, styles.groupRow)}
                                    onClick={() => toggleIds(regionIds)}
                                >
                                    <CheckIcon state={stateOf(regionIds, selectedIds)} />
                                    <span className={styles.groupName}>{region}</span>
                                    <span className={styles.meta}>{regionLocations.length}</span>
                                </button>
                            )}

                            {regionLocations.map(location => {
                                const disabled = isDisabled?.(location) ?? false;

                                return (
                                    <button
                                        key={location.id}
                                        type="button"
                                        disabled={disabled}
                                        className={clsx(styles.row, styles.locationRow, {
                                            [styles.disabled]: disabled
                                        })}
                                        onClick={() => toggleIds([location.id])}
                                    >
                                        <CheckIcon
                                            state={selectedIds.includes(location.id) ? 'checked' : 'unchecked'}
                                        />
                                        <span className={styles.locationText}>
                                            <span className={styles.name}>{location.name}</span>
                                            <span className={styles.city}>{location.city}</span>
                                        </span>
                                        {renderMeta?.(location)}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}

                {groups.length === 0 && <p className={styles.empty}>No locations match “{search}”.</p>}
            </div>
        </div>
    );
};

export default LocationsChecklist;
